import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useAppContext } from '@/Main';
import { AiChatIcon } from '@/components/icons/tools';
import { getColor } from '@/helpers/utils';

export type SidebarBrowseMode = 'main' | 'ai';

type NavbarSidebarModeTabsProps = {
  value: SidebarBrowseMode;
  onChange: (mode: SidebarBrowseMode) => void;
};

/** تب‌های سایدبار با همان الگوی تب‌های ایندکسر (متن + خط زیر فعال). */
export function NavbarSidebarModeTabs({
  value,
  onChange,
}: NavbarSidebarModeTabsProps) {
  const {
    config: { theme },
  } = useAppContext() as { config: { theme: string } };

  const tabBg = (isActive: boolean) => {
    if (isActive && theme === 'dark') return 'rgba(0,0,0,.2)';
    if (isActive && theme === 'light') return 'rgba(0,0,0,.02)';
    return 'transparent';
  };

  const themeBorderColor = getColor('primary');

  const tabs: {
    id: SidebarBrowseMode;
    label: string;
    icon: 'th-large' | 'ai-chat';
    disabled?: boolean;
    /** جای برچسب کنار متن نیست؛ متن و برچسب به‌نوبت جای هم نمایش داده می‌شوند. */
    badge?: { type: string; text: string };
  }[] = [
    { id: 'main', label: 'منوی ابزار', icon: 'th-large' },
    {
      id: 'ai',
      label: 'چت',
      icon: 'ai-chat',
      badge: { type: 'success', text: 'جدید' },
    },
  ];

  return (
    <div className="navbar-sidebar-mode-tabs">
      <div
        className="d-flex overflow-y-hidden align-items-stretch border-bottom border-1 overflow-auto flex-nowrap"
        role="tablist"
        aria-label="حالت سایدبار"
      >
        {tabs.map(tab => {
          const isActive = value === tab.id && !tab.disabled;
          const isMuted = !isActive;
          const tabButton = (
            <button
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-disabled={tab.disabled || undefined}
              className={classNames(
                'navbar-sidebar-mode-tab bg-transparent fw-semi-bold',
                isActive && 'navbar-sidebar-mode-tab--active active-l text-800',
                tab.disabled && 'navbar-sidebar-mode-tab--disabled',
                isMuted && 'navbar-sidebar-mode-tab--inactive',
              )}
              style={{
                textDecoration: 'none',
                marginBottom: -1,
                padding: '0.45rem 0.85rem 0.2rem',
                backgroundColor: tabBg(isActive),
                borderTop: 0,
                borderLeft: 0,
                borderRight: 0,
                borderBottomWidth: 4,
                borderBottomStyle: 'solid',
                borderBottomColor: isActive ? themeBorderColor : 'transparent',
                color: isMuted ? 'var(--falcon-gray-600, #748194)' : undefined,
                opacity: isMuted ? 0.75 : 1,
              }}
              onClick={() => {
                if (!tab.disabled) onChange(tab.id);
              }}
            >
              <span className="d-inline-flex align-items-center gap-1">
                {tab.icon === 'ai-chat' ? (
                  <AiChatIcon size={16} active={false} lineWidth={5.5} />
                ) : (
                  <FontAwesomeIcon
                    icon={tab.icon}
                    className="fs--1"
                    style={isMuted ? { color: 'inherit', opacity: 1 } : undefined}
                  />
                )}
                <span
                  className={classNames(
                    'navbar-sidebar-mode-tab__label',
                    tab.badge && 'navbar-sidebar-mode-tab__label--swap',
                  )}
                  style={{
                    fontSize: '.8rem',
                    whiteSpace: 'nowrap',
                    color: 'inherit',
                  }}
                >
                  {tab.badge ? (
                    <>
                      <span className="navbar-sidebar-mode-tab__swap-text">
                        {tab.label}
                      </span>
                      <span
                        className="navbar-sidebar-mode-tab__swap-badge"
                        aria-hidden="true"
                      >
                        <span
                          className={`badge badge-subtle-${tab.badge.type} rounded-pill`}
                        >
                          {tab.badge.text}
                        </span>
                      </span>
                    </>
                  ) : (
                    tab.label
                  )}
                </span>
              </span>
            </button>
          );

          if (tab.disabled) {
            return (
              <OverlayTrigger
                key={tab.id}
                placement="left"
                popperConfig={{ strategy: 'fixed' }}
                overlay={
                  <Tooltip
                    id={`sidebar-tab-${tab.id}`}
                    style={{ position: 'fixed' }}
                  >
                    به زودی
                  </Tooltip>
                }
              >
                <span className="navbar-sidebar-mode-tab-wrap d-flex flex-grow-1">
                  {tabButton}
                </span>
              </OverlayTrigger>
            );
          }

          return (
            <span key={tab.id} className="navbar-sidebar-mode-tab-wrap d-flex flex-grow-1">
              {tabButton}
            </span>
          );
        })}
      </div>
    </div>
  );
}
